import React from 'react';
import PropTypes from 'prop-types';
import { Box, Button, Divider, Grid, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { formatAmount } from 'src/utils/formatAmout';
import theme from 'src/theme';
import { selectAvalQuoteInformation } from 'src/store/selectors/avalBuroFormSelectors';
import Loading from 'src/components/Loading';
import useStyles from '../styles';

const StepZero = ({ onNext }) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const quoteInformation = useSelector(selectAvalQuoteInformation);

  if (!quoteInformation) return <Loading />;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Typography variant="h3" sx={{ marginBottom: '10px', color: theme.palette.primary.main }}>
        {t('AVAL_BURO_FORM.STEP_ZERO.TITLE')}
      </Typography>

      <Typography variant="body2" sx={{ color: theme.palette.text.secondary, marginBottom: '16px' }}>
        {t('AVAL_BURO_FORM.STEP_ZERO.DESCRIPTION')}
      </Typography>

      <Box
        sx={{
          backgroundColor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          overflow: 'hidden',
          marginBottom: '24px'
        }}
      >
        <Box sx={{ px: { xs: 2, sm: 2.5 }, py: 1.5 }}>
          <Typography variant="caption" sx={{ fontWeight: 600, letterSpacing: '0.08em', color: 'text.secondary', textTransform: 'uppercase' }}>
            {t('AVAL_BURO_FORM.STEP_ZERO.SUMMARY_LABEL')}
          </Typography>
        </Box>

        <Divider />

        <Box sx={{ px: { xs: 2, sm: 2.5 }, py: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              {t('AVAL_BURO_FORM.STEP_ZERO.CLIENT_LABEL')}
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 600, mt: 0.25 }}>
              {quoteInformation.clientName}
            </Typography>
          </Box>

          <Divider />

          <Box>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              {t('AVAL_BURO_FORM.STEP_ZERO.EQUIPMENT_LABEL')}
            </Typography>
            {quoteInformation.equipments.map(eq => (
              <Typography key={`${eq.brandName}-${eq.equipmentType}-${eq.model}`} variant="body1" sx={{ mt: 0.25 }}>
                {eq.brandName}
                {' '}
                {eq.equipmentType}
                {' '}
                —
                {' '}
                {eq.model}
              </Typography>
            ))}
          </Box>

          <Divider />

          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                {t('AVAL_BURO_FORM.STEP_ZERO.AMOUNT')}
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 600, mt: 0.25 }}>
                {formatAmount(quoteInformation.price)}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                {t('AVAL_BURO_FORM.STEP_ZERO.TERM')}
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 600, mt: 0.25 }}>
                {quoteInformation.term}
                {' '}
                {t('COMMON.MONTHS')}
              </Typography>
            </Grid>
          </Grid>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: { xs: 'stretch', sm: 'flex-end' } }}>
        <Button
          className={`${classes.buttons} ${classes.buttonMain}`}
          onClick={onNext}
          color="primary"
          variant="contained"
          sx={{ width: { xs: '100%', sm: 'auto' }, px: { sm: 4 } }}
        >
          {t('COMMON.CONTINUE')}
        </Button>
      </Box>
    </Box>
  );
};

StepZero.propTypes = {
  onNext: PropTypes.func.isRequired
};

export default StepZero;
